import { formatDate, formatDateRange, formatDateTime, formatTime, MISSING } from '../../utils/format';
import { addDaysToYmd } from '../../utils/easternTime';
import { AUTH_ROLE_LABELS } from '../../utils/roles';
import type { TimeOffEntry } from '../../types/timeOff';

/* ------------------------------------------------------------------------ */
/* Entry notes: pull machine boilerplate out of what people wrote             */
/* ------------------------------------------------------------------------ */

const IMPORT_PREFIX = /^Imported from OOO calendar:\s*/i;
const NOT_COUNTED_SUFFIX = /\s*\(below 4 hours; not counted\)\s*$/i;

/** Policy sentences the backend appends to notes after " | ", as short flags. */
const POLICY_NOTES: { pattern: RegExp; flag: string }[] = [
  { pattern: /PTO requires 2-week advance notice\.?/i, flag: 'Short notice' },
  { pattern: /WFH requires VP approval for non-eligible employees\.?/i, flag: 'Needs VP approval' },
  { pattern: /Recurring Outlook calendar series\.?/i, flag: 'Recurring series' },
];

export const NOT_COUNTED_FLAG = 'Under 4 hrs, not counted';

export interface EntryText {
  /** The event subject and/or what a person wrote; null when there's neither. */
  primary: string | null;
  /** Short facts lifted out of the notes: "Short notice", "Recurring series"… */
  flags: string[];
}

const sameText = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * Calendar imports store notes like
 * "Imported from OOO calendar: ooo – dentist (below 4 hours; not counted) | PTO requires 2-week advance notice."
 * This keeps the subject, drops the boilerplate, and turns the policy text into flags.
 */
export const describeEntryText = (notes?: string | null, subject?: string | null): EntryText => {
  const flags: string[] = [];
  let text = (notes || '').trim();

  POLICY_NOTES.forEach(({ pattern, flag }) => {
    if (pattern.test(text)) {
      text = text.replace(pattern, '');
      flags.push(flag);
    }
  });
  text = text.replace(/(\s*\|\s*)+$/, '').trim();

  let importedSubject: string | null = null;
  if (IMPORT_PREFIX.test(text)) {
    text = text.replace(IMPORT_PREFIX, '');
    if (NOT_COUNTED_SUFFIX.test(text)) {
      text = text.replace(NOT_COUNTED_SUFFIX, '');
      flags.unshift(NOT_COUNTED_FLAG);
    }
    importedSubject = text.trim() || null;
    text = '';
  }

  const subjectText = (subject || '').trim() || importedSubject;
  const note = text && !(subjectText && sameText(text, subjectText)) ? text : null;
  return {
    primary: [subjectText, note].filter(Boolean).join(' · ') || null,
    flags,
  };
};

/* ------------------------------------------------------------------------ */
/* People                                                                     */
/* ------------------------------------------------------------------------ */

/** "employee" → "Employee", "jr_ae" → "Jr manager"; titles that already have capitals stay as written. */
export const displayRole = (role?: string | null) => {
  const raw = (role || '').trim();
  if (!raw) return null;
  const known = (AUTH_ROLE_LABELS as Record<string, string>)[raw.toLowerCase()];
  if (known) return known;
  if (raw !== raw.toLowerCase()) return raw;
  const text = raw.replace(/_/g, ' ');
  return text[0].toUpperCase() + text.slice(1);
};

export const isGenericRole = (role?: string | null) => (role || '').trim().toLowerCase() === 'employee';

/** Shows a person's name when a field holds their email and the directory knows them. */
export const resolvePersonName = (
  value: string | null | undefined,
  people: { name?: string | null; email?: string | null }[]
) => {
  const text = (value || '').trim();
  if (!text || !text.includes('@')) return text || null;
  const match = people.find((person) => person.email && person.email.toLowerCase() === text.toLowerCase());
  return match?.name || text;
};

/* ------------------------------------------------------------------------ */
/* Request errors: say why, and what the person can do                        */
/* ------------------------------------------------------------------------ */

type ErrorKind = 'network' | 'server' | 'input' | 'session' | 'forbidden' | 'missing';

const REASONS: Record<ErrorKind, string> = {
  network: "The server didn't respond.",
  server: 'The server ran into a problem.',
  input: "Some details weren't accepted.",
  session: 'Your session has expired.',
  forbidden: "You don't have permission for this.",
  missing: 'The record no longer exists.',
};

const asSentence = (text: string) => {
  const trimmed = text.trim();
  if (!trimmed) return trimmed;
  const capped = trimmed[0].toUpperCase() + trimmed.slice(1);
  return /[.?]$/.test(capped) ? capped : `${capped.replace(/!+$/, '')}.`;
};

const classifyError = (error: unknown): { kind: ErrorKind; message: string | null } => {
  const { status, data } = (error || {}) as { status?: unknown; data?: unknown };
  const body = (data && typeof data === 'object' ? data : {}) as { error?: unknown; message?: unknown };
  const message = typeof body.error === 'string' && body.error.trim()
    ? body.error
    : typeof body.message === 'string' && body.message.trim()
      ? body.message
      : null;
  if (typeof status === 'number') {
    if (status === 401) return { kind: 'session', message };
    if (status === 403) return { kind: 'forbidden', message };
    if (status === 404) return { kind: 'missing', message };
    if (status >= 400 && status < 500) return { kind: 'input', message };
    if (status >= 500) return { kind: 'server', message };
  }
  if (status === 'PARSING_ERROR') return { kind: 'server', message: null };
  return { kind: 'network', message: null };
};

/**
 * "Why + what to do" for an RTK Query error. Validation (4xx) responses show the
 * server's own reason, so the person can fix their input instead of retrying.
 */
export const explainRequestError = (
  error: unknown,
  { retry = 'Try again in a minute.', fix = 'Check the details and try again.' }: { retry?: string; fix?: string } = {}
) => {
  const { kind, message } = classifyError(error);
  const reason = (kind === 'input' || kind === 'forbidden') && message ? asSentence(message) : REASONS[kind];
  const next = kind === 'input'
    ? fix
    : kind === 'session'
      ? 'Sign in again to continue.'
      : kind === 'forbidden'
        ? 'Ask HR or an admin if you need access.'
        : kind === 'missing'
          ? 'Refresh the page to see the latest.'
          : retry;
  return `${reason} ${next}`;
};

/* ------------------------------------------------------------------------ */
/* Calendar event times (Microsoft Graph dateTime + time zone)                */
/* ------------------------------------------------------------------------ */

const UTC_ZONES = new Set(['utc', 'etc/utc', 'gmt', 'etc/gmt', 'z', 'coordinated universal time']);

/** Windows zone names Graph can return, mapped to IANA. */
const WINDOWS_ZONES: Record<string, string> = {
  'eastern standard time': 'America/New_York',
  'central standard time': 'America/Chicago',
  'mountain standard time': 'America/Denver',
  'us mountain standard time': 'America/Phoenix',
  'pacific standard time': 'America/Los_Angeles',
  'gmt standard time': 'Europe/London',
};

const HAS_OFFSET = /(Z|[+-]\d{2}:?\d{2})$/i;
const WALL_TIME = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/;

/** Milliseconds `zone` is ahead of UTC at `instant`. Throws on an unknown zone. */
const zoneOffsetMs = (instant: number, zone: string) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    hour12: false,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  }).formatToParts(new Date(instant));
  const get = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? 0);
  return Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'), get('second')) - instant;
};

/** Reads "2026-09-16T16:15:00.0000000" in the event's own zone (UTC when missing). */
export const parseCalendarDateTime = (value?: string | null, zone?: string | null): Date | null => {
  const text = (value || '').trim();
  if (!text) return null;
  if (HAS_OFFSET.test(text)) {
    const date = new Date(text);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const match = WALL_TIME.exec(text);
  if (!match) return null;
  const [year, month, day, hour, minute, second] = match.slice(1).map((part) => Number(part || 0));
  const wallAsUtc = Date.UTC(year, month - 1, day, hour, minute, second);
  if (Number.isNaN(wallAsUtc)) return null;
  const rawZone = (zone || '').trim();
  if (!rawZone || UTC_ZONES.has(rawZone.toLowerCase())) return new Date(wallAsUtc);
  const ianaZone = WINDOWS_ZONES[rawZone.toLowerCase()] ?? rawZone;
  try {
    const firstGuess = wallAsUtc - zoneOffsetMs(wallAsUtc, ianaZone);
    return new Date(wallAsUtc - zoneOffsetMs(firstGuess, ianaZone));
  } catch (error) {
    return null;
  }
};

const splitPeriod = (time: string) => {
  const match = /^(.*?)\s*([AP]M)$/i.exec(time);
  return match ? { clock: match[1], period: match[2] } : { clock: time, period: '' };
};

/** "Sep 16, 2026, 12:15–1:45 PM ET" · "All day · Sep 16–17, 2026" · null when the entry has no event times. */
export const formatCalendarEventTime = (entry: TimeOffEntry): string | null => {
  if (!entry.originalCalendarStart && !entry.originalCalendarEnd) return null;
  const ymd = /^\d{4}-\d{2}-\d{2}$/;

  if (entry.originalCalendarIsAllDay) {
    const startYmd = (entry.originalCalendarStart || '').slice(0, 10);
    const endRaw = (entry.originalCalendarEnd || '').slice(0, 10);
    if (!ymd.test(startYmd)) return MISSING;
    // Graph ends all-day events at midnight the next day.
    const endYmd = ymd.test(endRaw) && endRaw > startYmd ? addDaysToYmd(endRaw, -1) : startYmd;
    return `All day · ${formatDateRange(startYmd, endYmd)}`;
  }

  const zone = entry.originalCalendarTimeZone;
  const start = parseCalendarDateTime(entry.originalCalendarStart, zone);
  const end = parseCalendarDateTime(entry.originalCalendarEnd, zone);
  if (!start && !end) return MISSING;
  if (!start || !end) return formatDateTime((start || end) as Date);

  if (formatDate(start) === formatDate(end)) {
    const startTime = splitPeriod(formatTime(start).replace(/\s*ET$/, ''));
    const endTime = splitPeriod(formatTime(end).replace(/\s*ET$/, ''));
    const startText = startTime.period && startTime.period === endTime.period
      ? startTime.clock
      : `${startTime.clock} ${startTime.period}`.trim();
    return `${formatDate(start)}, ${startText}–${formatTime(end)}`;
  }
  return `${formatDateTime(start)} – ${formatDateTime(end)}`;
};
